import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ProductSchemaForm from './ProductSchemaForm';
import FaqSchemaForm from './FaqSchemaForm';
import GeneratedSchemas from './GeneratedSchemas';
import { validateSchema } from './validateSchema';

import {
  Container,
  Typography,
  Button,
  Grid,
  Box,
  Alert,
  Divider,
  Card,
  CardContent,
  CardHeader,
  useTheme,
  alpha,
  IconButton,
  Collapse
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';

// Styled Button
const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.common.white,
  fontWeight: 600,
  padding: '10px 24px',
  borderRadius: 8,
  boxShadow: theme.shadows[2],
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
    boxShadow: theme.shadows[4]
  }
}));

// Reusable Form Section
const FormSection = ({ title, description, children, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const theme = useTheme();

  return (
    <Card
      sx={{
        mb: { xs: 25, sm: 4 },
        borderRadius: { xs: 1.5, sm: 2 },
        boxShadow: theme.shadows[1],
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        px: { xs: 1.5, sm: 2.5 },
        py: { xs: 1.5, sm: 1 },
        width: '100%',
        overflowX: 'auto'
      }}
    >
      <CardHeader
        title={title}
        subheader={description}
        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
        subheaderTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
        action={
          <IconButton onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        }
        sx={{
          backgroundColor: alpha(theme.palette.primary.light, 0.05),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      />
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 1 }}>{children}</CardContent>
      </Collapse>
    </Card>
  );
};

const Seo = ({seoData, setSeoData}) => {
  const { id } = useParams(); // 👈 get dynamic ID from URL
  const theme = useTheme();

  console.log('ID:', id)
  //const [seoData, setSeoData] = useState({ schema: [] });
  const [generatedSchemas, setGeneratedSchemas] = useState([]);
  const [validationResults, setValidationResults] = useState([]); 

  useEffect(() => { 
    if (!id) return;

    const fetchSchemaData = async () => {
      try {
        const response = await axios.get(`/api/getseo/${id}`);
        const data = response.data;
        setSeoData(data.seo);
          console.log('ID:', id)

        const rawSchema = data.seo?.schema || [];

        if (Array.isArray(rawSchema)) {
          setGeneratedSchemas(rawSchema);
        } else if (rawSchema && typeof rawSchema === 'object') {
          setGeneratedSchemas([{ type: 'product', schema: rawSchema }]);
        } else {
          setGeneratedSchemas([]);
        }
      } catch (error) {
        console.error('Error fetching schema data:', error);
        setSeoData({ schema: [] });
        setGeneratedSchemas([]);
      }
    };

    fetchSchemaData();
  }, [id]);

  const handleSchemaGeneration = (schema, type) => {
    const updatedSchemas = generatedSchemas.some(item => item.type === type)
      ? generatedSchemas.map(item =>
          item.type === type ? { type, schema } : item
        )
      : [...generatedSchemas, { type, schema }];

    setGeneratedSchemas(updatedSchemas);
    setSeoData(prev => ({
      ...prev,
      schema: updatedSchemas
    }));
  };

  const handleSchemaValidation = () => {
    const results = generatedSchemas.map(item => {
      const isValid = validateSchema(item.schema, item.type);
      return {
        type: item.type,
        isValid,
        errors: isValid ? [] : ['Schema is invalid.']
      };
    });

    setValidationResults(results);
    setSeoData(prev => ({
      ...prev,
      schema: generatedSchemas
    }));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Title */}
      <Box textAlign="center" mb={4}>
        <Typography
          variant="h4"
          fontWeight="bold"
          gutterBottom
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block'
          }}
        >
          SEO Schema Generator
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create structured data to enhance your search engine visibility
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Product Schema Section */}
      <FormSection
        title="Product Schema"
        description="Add structured data for your products to appear in rich results"
        defaultExpanded={false}
      >
        <ProductSchemaForm
          onGenerate={handleSchemaGeneration}
          initialSchema={generatedSchemas.find(item => item.type === 'product')?.schema}
        />
      </FormSection>

      {/* FAQ Schema Section */}
      <FormSection
        title="FAQ Schema"
        description="Create FAQ structured data for your content"
        defaultExpanded={false}
      >
        <FaqSchemaForm
          onGenerate={handleSchemaGeneration}
          initialSchema={generatedSchemas.find(item => item.type === 'faq')?.schema}
        />
      </FormSection>

      {/* Generated Schemas Section */}
      <FormSection
        title="Generated Schemas"
        description="Preview and validate your structured data"
        defaultExpanded={false}
      >
        <Box mb={3}>
          {generatedSchemas.length > 0 ? (
            <GeneratedSchemas schemas={generatedSchemas} />
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontStyle: 'italic' }}
            >
              No schema available to display.
            </Typography>
          )}
        </Box>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <GradientButton
            variant="contained"
            onClick={handleSchemaValidation}
            size="large"
            sx={{ minWidth: 200 }}
          >
            Validate All Schemas
          </GradientButton>
        </Box>

        <Box sx={{ mt: 3 }}>
          {validationResults.length > 0 &&
            validationResults.map((result, index) => (
              <Alert
                key={index}
                severity={result.isValid ? 'success' : 'error'}
                sx={{
                  mt: 2,
                  borderRadius: 1,
                  alignItems: 'center'
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  sx={{ display: 'inline', mr: 1 }}
                >
                  {result.type} Schema:
                </Typography>
                <Typography variant="body2" sx={{ display: 'inline' }}>
                  {result.isValid
                    ? 'Valid schema! Ready for implementation'
                    : 'Invalid schema. Please check the errors.'}
                </Typography>
              </Alert>
            ))}
        </Box>
      </FormSection>
    </Container>
  );
};

export default Seo;
